import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import AsesorasConReparto from "../../../promos/asesorasConReparto";
import CursoRegalado2x1 from "../../../promos/cursoRegalado2x1";
import { Bloqueo, EligeRegalo, FitPill, LineasExtra, TarjetaPromo, cursoDe, dinero, regala } from "../../../promos/tarjetasPromo";
import { Pill } from "../../../promos/tarjetasPromo";
import { EnCamino, KIND_DOS_X_UNO, PromoApp, PromoAppliedPaid, PromoDraft, PromoOption, chargeOf, fitOf, giftOptionsOf, initialGiftKey, promoPending, round2 } from "../../../promos/tipos";
import R from "./strings";

/*
	«Aplicar Promoción» (docs/ESPEC_PROMOS_ADELANTO.md §5 C1): las promos de adelanto (meses gratis, kit, 2x1, todos juntos) se aplican
	SOLO aquí y siempre junto con el pago que las completa. WAPP sugiere la promo por el monto del pago (GET payments/_promoOptions);
	al escoger una, el modal marca y bloquea los renglones `payKeys` completos y manda `promoApplication` con el pago. El servidor
	vuelve a validar todo (candado de la 3ra mensualidad, monto, una promo por matrícula).

	Desde 2026-10-31 (ESPEC_SOLICITUD_PROMO front 6) el botón vive en el encabezado de la persona (lo pinta el modal con `showButton`
	= false y `onHasOptions`) y, con un monto escrito y el panel cerrado, se pregunta UNA vez (700 ms después de dejar de teclear) si
	alguna promo cuadra exacto: si sí, el aviso dorado «Este monto cuadra con «8x4»» con «Aplicar». Nunca con monto 0.
*/

/** Si `_promoOptions` no lista la promo de una En Camino viva (ya está en marcha), el panel se arma con lo que dice la En Camino. */
const opcionDeEnCamino = (e: EnCamino): PromoOption => ({
	enrollmentId: e.enrollmentId, studentId: e.studentId, campusId: e.campusId, courseName: e.courseName || "", scheduleText: e.scheduleText || "", promotionId: e.promotionId,
	name: e.promoName, kind: e.kind || "meses", needed: e.remaining, deadline: e.dueDate || null, blocked: null, fits: null, diff: null,
	payKeys: e.payKeys || [], replaces: null, reopens: 0,
});

export type { PromoApp, PromoAppliedPaid, PromoDraft, PromoOption, GiftOption, GiftProduct, ReopenedMonth } from "../../../promos/tipos";
export { chargeOf, fitOf, giftOptionsOf, initialGiftKey, promoPending } from "../../../promos/tipos";

const http = HttpManager.getInstance();

/** Lo que regala, en corto, para el aviso: «8 meses» o lo que diga la promo. */
export const resumenRegalo = (o: PromoOption, sym: string): string => {
	const meses = o.gives?.months?.length || 0;
	if (meses > 0 && !giftOptionsOf(o).length) return meses + (meses === 1 ? " mes" : " meses");
	return regala(o, sym, initialGiftKey(o));
};

interface Props {
	campusId: number;
	student: { id: number };
	sym: string;
	/** Monto escrito o leído del pago. */
	amount: number;
	paymentDate: string;
	intentId: number | null;
	open: boolean;
	/** Abre solo (ticket con borrador): carga las opciones sin que la cajera abra el panel. */
	preload: boolean;
	/** Promo guardada en el borrador del ticket (o pedida desde el aviso de adelanto); manda sobre el `draft` del servidor. */
	restore: PromoDraft | null;
	/** Cambia para volver a precargar (aviso de adelanto pendiente, draft de otra persona). */
	preloadKey?: string;
	/** El draft es de otra persona del pago (2x1: el ticket es de quien recibe): la pantalla abre el panel en quien paga. */
	onForeignDraft?: (d: PromoDraft) => void;
	selected: PromoApp | null;
	/** Lo que se cobra en todo el pago (renglones de la promo + lo demás marcado): es lo que valida el servidor y contra lo que cuadra la píldora. */
	seCobra: number;
	/** Recargos de la promo que se anulan solos por pagar a tiempo (no se cobran): se restan del `charge` del servidor en la lista. */
	recargosATiempo?: (o: PromoOption) => number;
	/** Ya hay una promo en otra persona de este pago. */
	otherSelected: boolean;
	movil: boolean;
	busy: boolean;
	serverError: (e: any) => string;
	onOpen: () => void;
	onClose: () => void;
	onSelect: (option: PromoOption, draft: PromoDraft | null, enCamino?: EnCamino | null) => void;
	onRefresh: (option: PromoOption) => void;
	onClear: () => void;
	onChange: (patch: Partial<PromoApp>) => void;
	/** «Aplicar Sin Cobrar» terminó bien: el pago se limpia y se recarga la persona. */
	onApplied: (option: PromoOption, res: PromoAppliedPaid | null) => void;
	/** false = el botón «Aplicar Promoción» lo pinta quien usa el panel (en el encabezado de la persona); aquí solo el aviso de «cuadra». */
	showButton?: boolean;
	/** false = la cajera ya quitó la promo: no se vuelve a precargar la En Camino viva. */
	enCaminoOk?: boolean;
	/** Las promos En Camino / Por Cobrar vivas de esta persona (para el aviso dorado de arriba de su caja). */
	onEnCamino?: (list: EnCamino[]) => void;
	/** Lo que el pago aplica a los meses de la En Camino (lo recibido sin recargos ni lo de otras personas) contra lo que falta: «Completa» o «Abono». */
	recibido?: number;
	/** Si esta persona tiene promos que ofrecer (null = no se sabe todavía): para pintar o no el botón de afuera. */
	onHasOptions?: (has: boolean | null) => void;
}

const AplicarPromocion: React.FC<Props> = ({ campusId, student, sym, amount, paymentDate, intentId, open, preload, restore, preloadKey, onForeignDraft, selected, seCobra, recargosATiempo, otherSelected,
	movil, busy, serverError, onOpen, onClose, onSelect, onRefresh, onClear, onChange, onApplied, showButton = true, enCaminoOk = true, onEnCamino, recibido = 0, onHasOptions }) => {
	const [data, setData] = useState<PromoOption[] | null>(null);
	/** null = no se sabe todavía; false = esta persona no tiene nada que ofrecer (no se muestra el enlace). */
	const [hasOptions, setHasOptions] = useState<boolean | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [verOtras, setVerOtras] = useState<boolean>(false);
	const [applying, setApplying] = useState<boolean>(false);
	/** Sube para volver a pedir las opciones (tras «Aplicar Sin Cobrar»). */
	const [tick, setTick] = useState<number>(0);
	/** La consulta liviana encontró un borrador: ahora sí se pide la lista completa para precargarlo. */
	const [wantFull, setWantFull] = useState<boolean>(false);
	/** La promo que cuadra exacto con el monto escrito (aviso dorado con «Aplicar»). */
	const [sugerida, setSugerida] = useState<PromoOption | null>(null);
	/** Promos En Camino / Por Cobrar vivas de la persona (las manda `_promoOptions.enCamino`). */
	const [vivas, setVivas] = useState<EnCamino[]>([]);
	/** La consulta liviana vio una En Camino viva: se precarga como un borrador de ticket. */
	const [ecLive, setEcLive] = useState<boolean>(false);
	const pre = useRef<boolean>(false);
	const preloadEff = preload || (ecLive && enCaminoOk);
	const guardaVivas = (list: any) => { const l: EnCamino[] = Array.isArray(list) ? list : []; setVivas(l); onEnCamino?.(l); setEcLive(l.some((x) => Number(x.studentId) === student.id)); };
	useEffect(() => { pre.current = false; setWantFull(false); }, [preloadKey]);
	useEffect(() => { onHasOptions?.(hasOptions); }, [hasOptions]); // eslint-disable-line react-hooks/exhaustive-deps
	// Lista completa: solo cuando la cajera abre el panel, hay una promo escogida o hay un borrador que precargar (y solo una vez).
	const full = open || !!selected || (preloadEff && !pre.current && (!!restore || wantFull));
	// Consulta liviana (`onlyDraft=1`): ticket con borrador/solicitud al abrirse; dice si hay borrador y si vale la pena mostrar el enlace.
	const light = !full && preloadEff && !pre.current;
	// El monto solo cambia lo que la lista dice de «cuadra»: se repinta aquí; únicamente con el panel abierto se vuelve a pedir si lo teclean.
	const amountDep = open && !selected ? amount : 0;

	// Persona sin ticket ni borrador: una consulta liviana para saber si tiene una En Camino viva (aviso dorado + panel precargado).
	useEffect(() => {
		if (preload || full) return;
		let dead = false;
		http.paymentService.promoOptions(campusId, { studentId: student.id, amount: 0, paymentDate, intentId, onlyDraft: true }).then((r: any) => {
			if (dead) return;
			guardaVivas(r.data?.enCamino);
			if (r.data?.draft?.enCaminoId) setWantFull(true);
		}).catch(() => { /* sin esta consulta no hay aviso; el pago sigue igual */ });
		return () => { dead = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, student.id, intentId]);

	useEffect(() => {
		if (!light) return;
		let dead = false;
		http.paymentService.promoOptions(campusId, { studentId: student.id, amount: 0, paymentDate, intentId, onlyDraft: true }).then((r: any) => {
			if (dead) return;
			setHasOptions(r.data?.hasOptions !== false);
			setError(null);
			if (r.data?.enCamino) guardaVivas(r.data.enCamino);
			if (r.data?.draft) setWantFull(true);
			else pre.current = true;
		}).catch((e: any) => { if (!dead) setError(serverError(e)); });
		return () => { dead = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [light, campusId, student.id, intentId, preloadKey]);

	useEffect(() => {
		if (!full) return;
		let dead = false;
		const t = setTimeout(() => {
			http.paymentService.promoOptions(campusId, { studentId: student.id, amount: amountDep, paymentDate, intentId }).then((r: any) => {
				if (dead) return;
				const options: PromoOption[] = r.data?.options || [];
				setData(options);
				setHasOptions(options.length > 0 || r.data?.hasOptions !== false || (r.data?.enCamino || []).length > 0);
				setError(null);
				if (r.data?.enCamino !== undefined) guardaVivas(r.data.enCamino);
				if (selected) {
					const same = options.find((o) => o.enrollmentId === selected.option.enrollmentId && o.promotionId === selected.option.promotionId);
					if (same) onRefresh(same);
				} else if (!pre.current && preloadEff) {
					pre.current = true;
					const d: PromoDraft | null = restore || r.data?.draft || null;
					if (d) {
						// Only an En Camino this person pays: the 2x1 gift person never gets the payer's En Camino preselected.
						const ec: EnCamino | null = d.enCaminoId ? (r.data?.enCamino || []).find((x: EnCamino) => Number(x.id) === Number(d.enCaminoId) && Number(x.studentId) === student.id) || null : null;
						const o = options.find((x) => x.enrollmentId === Number(d.enrollmentId) && x.promotionId === Number(d.promotionId)) || (ec ? opcionDeEnCamino(ec) : undefined);
						if (o) onSelect(o, d, ec);
						else if (d.studentId && Number(d.studentId) !== student.id && onForeignDraft) onForeignDraft(d);
					}
				}
			}).catch((e: any) => { if (!dead) setError(serverError(e)); });
		}, data === null ? 0 : 500);
		return () => { dead = true; clearTimeout(t); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [full, amountDep, paymentDate, campusId, student.id, preloadKey, tick]);

	/*
		Aviso «cuadra»: panel cerrado, sin promo escogida y monto > 0 → una sola consulta por cambio de monto (700 ms después de dejar de
		teclear), por persona (cada instancia es una persona). Con monto 0 nunca se pregunta ni se enseña.
	*/
	const quieta = !open && !selected && !otherSelected && amount > 0.004 && hasOptions !== false;
	useEffect(() => {
		if (!quieta) { setSugerida(null); return; }
		let dead = false;
		const t = setTimeout(() => {
			http.paymentService.promoOptions(campusId, { studentId: student.id, amount, paymentDate, intentId }).then((r: any) => {
				if (dead) return;
				const options: PromoOption[] = r.data?.options || [];
				setHasOptions(options.length > 0 || r.data?.hasOptions !== false);
				setSugerida(options.find((o) => !o.blocked && !o.paidAlready && o.fits === "exacto") || null);
			}).catch(() => { if (!dead) setSugerida(null); });
		}, 700);
		return () => { dead = true; clearTimeout(t); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [quieta, amount, paymentDate, campusId, student.id]);

	const ecDe = (o: PromoOption): EnCamino | null => vivas.find((x) => x.enrollmentId === o.enrollmentId && x.promotionId === o.promotionId) || null;
	const esDosXUno = selected?.option.kind === KIND_DOS_X_UNO;
	useEffect(() => { if (!selected) setApplying(false); }, [!!selected]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => { if (!open) setVerOtras(false); }, [open]);

	const aplicarSinCobrar = async () => {
		if (!selected || applying) return;
		try {
			setApplying(true);
			setError(null);
			const r = await http.paymentService.promoApplyPaid(campusId, {
				studentId: selected.studentId, enrollmentId: selected.option.enrollmentId, promotionId: selected.option.promotionId,
				advisors: (selected.advisors || []).map((a) => ({ userId: a.userId, name: a.name, sharePercentage: a.sharePercentage })),
				...(selected.giftStudentId ? { giftStudentId: selected.giftStudentId } : {}),
				...(selected.giftEnrollmentId ? { giftEnrollmentId: selected.giftEnrollmentId } : {}),
				...(selected.giftOptionKey ? { giftOptionKey: selected.giftOptionKey } : {}),
				// Solicitud de Promoción: el servidor liga la promo al ticket y lo saca de la cola de Caja.
				...(intentId ? { intentId } : {}),
			});
			setData(null);
			setHasOptions(null);
			setTick((x) => x + 1);
			onApplied(selected.option, (r?.data as PromoAppliedPaid) || null);
		} catch (e) {
			setError(serverError(e));
		} finally {
			setApplying(false);
		}
	};

	/** Cuadra / Faltan / Sobra contra lo recibido. `need` = lo que se cobra (en la lista, lo que pide la promo; con la promo escogida, todo lo marcado en el pago). */
	const fitOfOption = (o: PromoOption, need: number) => {
		if (o.paidAlready) return { fits: "exacto" as const, diff: 0 };
		if (amount > 0.004) return fitOf(need, amount);
		return { fits: o.fits, diff: o.diff || 0 };
	};
	/** Lo que se cobra al escoger esta promo en la lista: `charge` del servidor (lo que pide + recargos) sin los recargos que se anulan por pagar a tiempo. */
	const cargoLista = (o: PromoOption): number => Math.max(0, round2(chargeOf(o) - (recargosATiempo ? recargosATiempo(o) : 0)));
	const recargoReal = (o: PromoOption): number => Math.max(0, round2((Number(o.surcharges) || 0) - (recargosATiempo ? recargosATiempo(o) : 0)));

	// ------------------------------------------------------------------ promo escogida
	if (selected) {
		const o = selected.option;
		const falta = promoPending(selected);
		const cobra = o.paidAlready ? 0 : seCobra;
		const ec = selected.enCamino || null;
		if (ec) {
			// En Camino: Caja solo escribe el monto; sin completar entra como abono, completando WAPP aplica la promo en ese mismo pago.
			const completa = recibido >= Number(ec.remaining) - 0.005 && recibido > 0.004;
			return (
				<div style={{ border: "1px solid var(--wa-acento)", background: "var(--wa-acento-suave)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
					<div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
						<b>{o.name}</b>
						<span style={{ display: "flex", gap: 8, alignItems: "center" }}>
							{recibido > 0.004 ? <Pill t={completa ? R.ecCompleta : R.ecAbono} tone={completa ? "acento" : "oro"} /> : null}
							<button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={onClear}>{R.promoRemove}</button></span>
					</div>
					<div style={{ color: "var(--wa-apagado)" }}>{cursoDe(o)}</div>
					<div>{R.ecSeCobra}: <b>{dinero(sym, cobra)}</b></div>
					{(ec.advisors || []).length ? <div style={{ color: "var(--wa-apagado)" }}>{R.ecAsesoras}: {(ec.advisors || []).map((a) => a.name).join(", ")}</div> : null}
					{esDosXUno && selected.giftLabel ? <div style={{ color: "var(--wa-apagado)" }}>{R.ecRegalado}: {selected.giftLabel}</div> : null}
					{o.blocked ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 6 }}>{o.blocked}</div> : null}
				</div>
			);
		}
		return (
			<div style={{ border: "1px solid var(--wa-acento)", background: "var(--wa-acento-suave)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
					<b>{o.name}</b>
					<span style={{ display: "flex", gap: 8, alignItems: "center" }}><FitPill o={o} fit={fitOfOption(o, cobra)} sym={sym} />
						<button type="button" className="btn btn-link btn-sm p-0" disabled={busy || applying} onClick={onClear}>{R.promoRemove}</button></span>
				</div>
				<div style={{ color: "var(--wa-apagado)" }}>{cursoDe(o)}</div>
				<div>Se Cobra: <b>{dinero(sym, cobra)}</b></div>
				<LineasExtra o={o} sym={sym} giftKey={selected.giftOptionKey} />
				<Bloqueo o={o} />
				<EligeRegalo o={o} sym={sym} giftKey={selected.giftOptionKey} disabled={busy || applying} onChange={(key) => onChange({ giftOptionKey: key })} />
				{esDosXUno ? (
					<CursoRegalado2x1 campusId={campusId} studentId={student.id} baseEnrollmentId={o.enrollmentId} giftEnrollmentId={selected.giftEnrollmentId}
						pinned={selected.giftEnrollmentId ? { enrollmentId: selected.giftEnrollmentId, studentId: selected.giftStudentId, name: selected.giftLabel || "Curso Regalado" } : null}
						disabled={busy || applying} serverError={serverError} onError={setError}
						onPick={(v) => onChange({ giftEnrollmentId: v.enrollmentId, giftStudentId: v.studentId || student.id, giftLabel: v.name + " " + (v.courseName || ""), giftIssues: null })}
						onIssues={(issues) => onChange({ giftIssues: issues })} />
				) : null}
				<div style={{ marginTop: 8 }}>
					<AsesorasConReparto campusId={o.campusId || campusId} value={selected.advisors} onChange={(list) => onChange({ advisors: list })} disabled={busy || applying} movil={movil} />
				</div>
				{falta && !o.blocked ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 6 }}>{falta}</div> : null}
				{o.paidAlready ? (
					<button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ marginTop: 8, minHeight: 40 }} disabled={busy || applying || !!falta} onClick={aplicarSinCobrar}>
						{applying ? "…" : R.promoApplyPaid}</button>
				) : null}
				{error ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 6 }}>{error}</div> : null}
			</div>
		);
	}

	// ------------------------------------------------------------------ cerrado: aviso de «cuadra» (y el botón, si lo pinta este componente)
	if (!open) {
		if (otherSelected || hasOptions === false) return null;
		return (
			<>
				{sugerida ? (
					<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 10px", marginTop: 8, fontSize: 13, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
						<span style={{ flex: 1, minWidth: 0 }}>{R.promoFits(sugerida.name, resumenRegalo(sugerida, sym))}</span>
						<button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ minHeight: movil ? 44 : 32 }} disabled={busy} onClick={() => onSelect(sugerida, null, ecDe(sugerida))}>{R.promoFitsApply}</button>
					</div>
				) : null}
				{showButton ? <button type="button" className="btn btn-outline-primary btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={onOpen}>{R.promoApply}</button> : null}
			</>
		);
	}

	// ------------------------------------------------------------------ lista de opciones
	// Primero lo que cuadra y lo acordado (máximo 3); las demás y las bloqueadas quedan bajo «Ver Otras».
	const rank = (o: PromoOption) => (fitOfOption(o, cargoLista(o)).fits === "exacto" ? 0 : o.agreed ? 1 : 2);
	const buenas = (data || []).filter((o) => !o.blocked).sort((a, b) => rank(a) - rank(b));
	const bloqueadas = (data || []).filter((o) => !!o.blocked);
	const visibles = buenas.slice(0, 3);
	const otras = [...buenas.slice(3), ...bloqueadas];
	const tarjeta = (o: PromoOption) => (
		<TarjetaPromo key={"o" + o.enrollmentId + "-" + o.promotionId} o={o} sym={sym} fit={fitOfOption(o, cargoLista(o))} cargo={cargoLista(o)} recargo={recargoReal(o)}
			accion={<button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ minHeight: 40 }} disabled={busy} onClick={() => onSelect(o, null, ecDe(o))}>
				{o.paidAlready ? R.promoApplyPaid : R.promoPick}</button>} />
	);
	return (
		<div style={{ border: "1px solid var(--wa-acento)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
				<b>{R.promoApply}</b>
				<button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={onClose}>{R.close}</button>
			</div>
			{data === null && !error ? <div style={{ color: "var(--wa-apagado)" }}>Cargando…</div> : null}
			{data !== null && data.length < 1 ? <div style={{ color: "var(--wa-apagado)" }}>{R.promoNone}</div> : null}
			{visibles.map(tarjeta)}
			{otras.length ? (
				<>
					<button type="button" className="btn btn-link btn-sm p-0" style={{ minHeight: movil ? 44 : undefined }} onClick={() => setVerOtras(!verOtras)}>
						{verOtras ? R.promoHideOthers : R.promoOthers(otras.length)}</button>
					{verOtras ? otras.map(tarjeta) : null}
				</>
			) : null}
			{error ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 6 }}>{error}</div> : null}
		</div>
	);
};

export default AplicarPromocion;
